import { type SyntheticEvent, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { Box, Container, Stack, Tab, Tabs } from "@mui/material";
import {
  Button,
  Icon,
  paletteVar,
  useDialogService,
} from "@platform/design-system";
import { PROPELX } from "@platform/feature-flags";
import { useQuery } from "@tanstack/react-query";

import { useRfpDetails } from "../../../../hooks/useRfpDetails";
import { ROUTE_NAMES } from "../../../../constants";

import { CarrierNotesDrawer } from "./CarrierNotesDrawer";
import { MpConfigSelect } from "./MpConfigSelect";
import { CarrierSequenceProvider } from "./CarrierSequenceContext";
import {
  NeedsReviewCommentsToolbarButton,
  useNeedsReviewComments,
} from "./NeedsReviewCommentsDrawer";
import { useResponsesTable } from "./tabs/Responses/useResponsesTable";

const SUB_TABS = [
  { label: "Responses", path: ROUTE_NAMES.RFP_WORKBENCH_PRESENT_RESPONSES },
  { label: "Comps", path: ROUTE_NAMES.RFP_WORKBENCH_PRESENT_COMPS },
  { label: "Financials", path: ROUTE_NAMES.RFP_WORKBENCH_PRESENT_FINANCIAL },
  { label: "Quotes", path: ROUTE_NAMES.RFP_WORKBENCH_PRESENT_QUOTES },
] as const;

/**
 * PresentWrapper - Protects the Present route and renders sub-tab navigation.
 *
 * Redirects to the Track route if the PropELX feature flag is not enabled.
 */
const PresentWrapper = (): React.JSX.Element | null => {
  const navigate = useNavigate();
  const { id: rfpId } = useParams();
  const { pathname } = useLocation();
  const isFeatureEnabled = useFeatureIsOn(PROPELX);
  const { openDialog } = useDialogService();
  // Subscribe to the prepareRfpDetails cache populated by BrokerRFPDetails.
  // `enabled: false` prevents this hook from fetching; it only reads cached
  // data and re-renders when the cache updates.
  const { data: rfpDetails } = useQuery<{
    broker?: { id?: number };
    data?: { broker?: { id?: number } };
  }>({
    queryKey: ["prepareRfpDetails", rfpId],
    queryFn: (): never => {
      throw new Error("cache-only query");
    },
    enabled: false,
  });
  // The broker's numeric id, not its D365/SF external id: template selection is
  // MP-configuration data keyed on the portal's own broker id.
  const broker = rfpDetails?.broker ?? rfpDetails?.data?.broker;
  const brokerId = broker?.id;

  const { data: carrierNoteRfpDetails } = useRfpDetails({
    rfpId: rfpId ?? "",
    enabled: !!rfpId && isFeatureEnabled,
  });
  const ecoRfpId = carrierNoteRfpDetails?.ecoRfpId ?? "";

  const { rowData } = useResponsesTable({
    rfpId: ecoRfpId,
    enabled: !!ecoRfpId && isFeatureEnabled,
  });

  const carriers = useMemo(
    () =>
      rowData.map((row) => ({
        carrierId: row.carrierId,
        carrierName: row.carrierName,
      })),
    [rowData],
  );

  const { totalCount: needsReviewCommentsCount } = useNeedsReviewComments({
    ecoRfpId: ecoRfpId || undefined,
  });

  useEffect(() => {
    if (!isFeatureEnabled) {
      navigate(
        `/${ROUTE_NAMES.RFPS}/${rfpId}/${ROUTE_NAMES.RFP_WORKBENCH_TRACK}`,
        { replace: true },
      );
    }
  }, [isFeatureEnabled, navigate, rfpId]);

  if (!isFeatureEnabled) {
    return null;
  }

  const basePath = `/${ROUTE_NAMES.RFPS}/${rfpId}/${ROUTE_NAMES.RFP_WORKBENCH_PRESENT}`;
  const lastSegment = pathname.split("/").pop();
  const activeTab =
    SUB_TABS.find((tab) => tab.path === lastSegment)?.path ?? SUB_TABS[0].path;

  const handleTabChange = (_: SyntheticEvent, value: string): void => {
    navigate(`${basePath}/${value}`);
  };

  // ecoRfpId is what the drawer reads the RFP's configuration (and so its
  // template) with, so opening before it resolves shows a "no template" warning
  // for an RFP that can in fact export. brokerId is checked by type rather than
  // against undefined: it comes from a loosely typed cache entry, so a null
  // would otherwise pass and reach the drawer.
  const canOpenGenerateExportMp = Boolean(
    rfpId && typeof brokerId === "number" && ecoRfpId,
  );

  const handleGenerateExportMp = (): void => {
    if (!canOpenGenerateExportMp) {
      return;
    }
    openDialog({
      type: "generateExportMp",
      // ecoRfpId lets the drawer read the RFP's configuration selection, which
      // carries the template it exports with.
      data: { rfpId, brokerId, ecoRfpId },
      width: 727,
      widthUnit: "px",
    });
  };

  // No internal guard needed here — the button below is disabled whenever
  // ecoRfpId is empty, and a disabled button never fires its click handler.
  const handleOpenNeedsReviewComments = (): void => {
    openDialog({
      type: "needsReviewComments",
      data: { ecoRfpId },
      width: 640,
      widthUnit: "px",
    });
  };

  return (
    <Box>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          // `grey.100` is a fixed scale value (same in both schemes); route to the
          // neutral surface token so the sub-tab bar follows the colour scheme.
          bgcolor: paletteVar("surfaces-neutral", "#f5f5f5"),
        }}
      >
        <Container
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="Present sub-tabs"
            textColor="secondary"
            indicatorColor="secondary"
            sx={{
              minHeight: 40,
              pl: 3,
              "& .MuiTabs-indicator": {
                height: 2,
                backgroundColor: "brandPrimary.main",
              },
              "& .MuiTab-root": { minHeight: 40, py: 1 },
              "& .MuiTab-root.Mui-selected": { color: "brandPrimary.main" },
            }}
          >
            {SUB_TABS.map((tab) => (
              <Tab key={tab.path} label={tab.label} value={tab.path} />
            ))}
          </Tabs>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ flexShrink: 0 }}
          >
            {ecoRfpId ? (
              <MpConfigSelect ecoRfpId={ecoRfpId} brokerId={brokerId} />
            ) : null}
            <NeedsReviewCommentsToolbarButton
              count={needsReviewCommentsCount}
              disabled={needsReviewCommentsCount === 0}
              onClick={handleOpenNeedsReviewComments}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleGenerateExportMp}
              disabled={!canOpenGenerateExportMp}
              startIcon={<Icon name="publishChanges" />}
              sx={{ flexShrink: 0 }}
            >
              Configure & Export MP
            </Button>
          </Stack>
        </Container>
      </Box>
      <Container sx={{ pt: 2, mb: 2 }}>
        <CarrierSequenceProvider key={rfpId} rfpId={rfpId ?? ""}>
          <Outlet />
        </CarrierSequenceProvider>
      </Container>
      {carriers.length > 0 && (
        <CarrierNotesDrawer carriers={carriers} ecoRfpId={ecoRfpId} />
      )}
    </Box>
  );
};

export default PresentWrapper;
